// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { BaseAvatar, Loading } from '~/components/common';
// Others
import { noteActions } from '~/thunks/note/noteThunk';
import { getDetailJob } from '~/thunks/job/jobThunk';
import { EMPTY_STRING } from '~/utils/constants/common';
import { IParamQueryJobDetail } from '~/utils/interface/jobs';
import { formatDate, getFullName } from '~/utils/helper/common';
import { IDetailNote, IGetListNoteByJob } from '~/utils/interface/note';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { DateFormatEnum, JobTabEnum, TypeJobDetailEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './NoteListTab.module.scss';

type Props = {
  id?: string;
  type?: JobTabEnum;
};

const cx = classNames.bind(styles);

const NoteListTab = (props: Props) => {
  //#region Destructuring Props
  const { id, type } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshNoteList } = useAppSelector((store) => store.note);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listNote, setListNote] = useState<IGetListNoteByJob>();
  const [params] = useState<IParamQueryJobDetail>({
    jobId: id || EMPTY_STRING,
    type: TypeJobDetailEnum.NOTES,
  });
  //#endregion Declare State

  //#region Declare Memo
  const dataNotes = useMemo(() => {
    switch (type) {
      case JobTabEnum.SITE_NOTES:
        return listNote?.siteNote || [];
      case JobTabEnum.EXTERNAL_NOTES:
        return listNote?.externalNote || [];
      case JobTabEnum.INTERNAL_NOTES:
        return listNote?.internalNote || [];
      default:
        return [];
    }
  }, [type, listNote]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListNotes(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!isRefreshNoteList) return;

    handleGetListNotes(params);

    dispatch(noteActions.setRefreshNoteList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshNoteList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListNotes = (params: IParamQueryJobDetail) => {
    setIsLoading(true);

    dispatch(getDetailJob(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        setListNote(res?.data as IGetListNoteByJob);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderTitle = () => {
    switch (type) {
      case JobTabEnum.SITE_NOTES:
        return t('tab_label_site_notes', { ns: 'common/base' });
      case JobTabEnum.EXTERNAL_NOTES:
        return t('tab_label_external_notes', { ns: 'common/base' });
      case JobTabEnum.INTERNAL_NOTES:
        return t('tab_label_internal_notes', { ns: 'common/base' });
      default:
        return EMPTY_STRING;
    }
  };

  const renderItem = (data: IDetailNote) => {
    return (
      <Stack className={cx('itemNote')}>
        <Stack direction={'row'} gap={'12px'}>
          <BaseAvatar dataAccount={data?.createdBy} />

          <Stack gap={'8px'} flex={1}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={'12px'}>
              <Typography className={cx('name')}>
                {getFullName({ firstName: data?.createdBy?.firstName, lastName: data?.createdBy?.lastName })}
              </Typography>

              <Typography className={cx('date')}>
                {formatDate(data?.createdAt, DateFormatEnum.MM_DD_YYYY_H_mm_A)}
              </Typography>
            </Stack>

            <Typography className={cx('value')}>{data?.content}</Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  //#endregion Handle Function

  return (
    <Stack id='taskJobDetailTabComponent' className={cx('container')}>
      <Typography className={cx('title')}>{renderTitle()}</Typography>

      <Stack className={cx('content')}>
        {dataNotes && dataNotes?.length > 0 ? (
          dataNotes.map((item) => renderItem(item))
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        )}
      </Stack>

      {isLoading && <Loading />}
    </Stack>
  );
};

export default NoteListTab;
